import { useCallback, useEffect, useState } from 'react';

const LANGUAGE_STORAGE_KEY = 'elmasa.language';
const DEFAULT_LANGUAGE = 'en';

const normalizeLanguage = (value, fallback = DEFAULT_LANGUAGE) => (
  value === 'ar' || value === 'en' ? value : fallback
);

const readStoredLanguage = (fallback = DEFAULT_LANGUAGE) => {
  if (typeof window === 'undefined') return fallback;
  return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY), fallback);
};

const writeStoredLanguage = (language) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
};

const usePersistentLanguage = (fallback = DEFAULT_LANGUAGE) => {
  const [language, setLanguageState] = useState(() => readStoredLanguage(fallback));

  const setLanguage = useCallback((nextLanguage) => {
    setLanguageState((currentLanguage) => {
      const resolvedLanguage =
        typeof nextLanguage === 'function'
          ? nextLanguage(currentLanguage)
          : nextLanguage;
      const normalizedLanguage = normalizeLanguage(resolvedLanguage, currentLanguage);
      writeStoredLanguage(normalizedLanguage);
      return normalizedLanguage;
    });
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage((currentLanguage) => (currentLanguage === 'en' ? 'ar' : 'en'));
  }, [setLanguage]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleStorage = (event) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        setLanguageState(normalizeLanguage(event.newValue, fallback));
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fallback]);

  return [language, setLanguage, toggleLanguage];
};

export default usePersistentLanguage;
